import AddElementLayout from "../layouts/AddElementLayout";
import * as Yup from 'yup';
import { confirmationRoles, ManagementRoles, phoneRegExp, roles, rolesConst } from "../../constants/others";
import TextInput from "../inputs/TextInput";
import { Button, Col, Row } from "antd";
import { openNotification } from "../../helpers/alerts";
import { postData, updateData, getData } from "../../helpers/api";
import SelectInput from "../inputs/SelectInput";
import MultipleSelect from "../inputs/MultipleSelect";
import { useEffect, useState } from "react";

const AddUser = (props) => {
    const {
        handleAfterAddingUser,
        initialValue,
        isModalOpen,
        handleCancel
    } = props
    const [selectedRole, setSelectedRole] = useState(initialValue.role || null);
    const [products, setProducts] = useState([]);
    const [managers, setManagers] = useState([]);
    const [deliveryCompanies, setDeliveryCompanies] = useState([]);
    const [deliveryCompanyKeys, setDeliveryCompanyKeys] = useState([]);

    // Fetch initial data
    useEffect(() => {
        if ([...confirmationRoles, rolesConst.Management].includes(selectedRole)) {
            getProducts();
            getDeliveryCompanies();
            getDeliveryCompanyKeys();
        }
        if (confirmationRoles.includes(selectedRole)) {
            getFallowAndManagementUsers();
        }
    }, [selectedRole]);

    // Update selected role when initialValue changes
    useEffect(() => {
        if (initialValue.role) {
            setSelectedRole(initialValue.role);
        }
    }, [initialValue.role]);

    const getProducts = async () => {
        try {
            const res = await getData("/products");
            setProducts(res.data.map((product) => ({ value: product.id, label: product.name })));
        } catch (error) {
            console.log(error);
        }
    };

    const getFallowAndManagementUsers = async () => {
        try {
            const res = await getData("users/roles/fallow/management");
            setManagers(res.data.map((ele) => ({ value: ele.id, label: ele.name })));
        } catch (error) {
            console.log(error);
        }
    };

    const getDeliveryCompanies = async () => {
        try {
            const res = await getData("/delivery-company");
            setDeliveryCompanies(res.data.filter((el) => el.name !== "Livreur")
                .map((ele) => ({ value: ele.id, label: ele.name })));
        } catch (error) {
            console.log(error);
        }
    };

    const getDeliveryCompanyKeys = async () => {
        try {
            const res = await getData("/delivery-company-keys");
            setDeliveryCompanyKeys(res.data.map((ele) => ({ value: ele.id, label: ele.name })));
        } catch (error) {
            console.log(error);
        }
    };

    const saveUser = async (values, submitProps) => {
        try {
            if (values.id) {
                delete values.edit;
                await updateData(`/users/${values.id}`, values);
                openNotification(`L'employeur a été mis a jour avec succès`, 'success');
            } else {
                await postData("/users", values);
                openNotification(`L'employeur a été ajouter avec succès`, 'success');
            }
            submitProps.resetForm();
            handleAfterAddingUser();
        } catch (error) {
            if (error.status === 409) {
                openNotification(`L'employeur existe déjà`, 'error');
                return;
            }
            openNotification(`Erreurs côté serveur`, 'error');
        }
    };

    // Prepare initial form values with existing data for edit mode
    const formInitialValues = {
        ...initialValue,
        managerId: initialValue.manager?.id || null,
        deliveryCompanyIds: initialValue.deliveryCompanies?.map(dc => dc.id) || [],
        deliveryCompanyKeyIds: initialValue.deliveryCompanyKeys?.map(key => key.id) || [],
        allowedProductIds: initialValue.allowedProducts?.map(p => p.id) || []
    };

    return (
        <AddElementLayout
            title={initialValue.id ? "Mis a jour l'employeur" : "Ajouter un employeur"}
            isModalOpen={isModalOpen}
            handleCancel={handleCancel}
            initialValue={formInitialValues}
            validationSchema={Yup.object({
                name: Yup.string().required('required'),
                prefix: Yup.string().required('required'),
                phone: Yup.string()
                    .required('required').matches(phoneRegExp, 'Le numéro de téléphone doit commencer par 06 ou 07 et comporter 10 chiffres.'),
                email: Yup.string().email('Invalid email address').required('Required'),
                city: Yup.string().required('required'),
                password: initialValue.id ? Yup.string() : Yup.string().required('required'),
                role: Yup.string().required('required'),
                managerId: Yup.number().when('role', {
                    is: (role) => confirmationRoles.includes(role),
                    then: () => Yup.number().required('required'),
                    otherwise: () => Yup.number().nullable()
                }),
                deliveryCompanyIds: Yup.array().when('role', {
                    is: (role) => [...confirmationRoles, rolesConst.Management].includes(role),
                    then: () => Yup.array().min(1, 'At least one company must be selected').required('Companies are required'),
                    otherwise: () => Yup.array().nullable()
                }),
                deliveryCompanyKeyIds: Yup.array().when('role', {
                    is: (role) => [...confirmationRoles, rolesConst.Management].includes(role),
                    then: () => Yup.array().min(1, 'At least one company key must be selected').required('Company keys are required'),
                    otherwise: () => Yup.array().nullable()
                }),
                paymentPerOrder: Yup.number().when('role', {
                    is: (role) => role !== rolesConst.Follow && role !== rolesConst.SuperAdmin,
                    then: () => Yup.number().required('required'),
                    otherwise: () => Yup.number().nullable()
                }),
                paymentPerUnitInOrder: Yup.number().when('role', {
                    is: (role) => role !== rolesConst.Follow && role !== rolesConst.Deliveryman && role !== rolesConst.SuperAdmin,
                    then: () => Yup.number().required('required'),
                    otherwise: () => Yup.number().nullable()
                }),
                paymentPerMoreThenOneProductInOrder: Yup.number().when('role', {
                    is: (role) => role !== rolesConst.Follow && role !== rolesConst.Deliveryman && role !== rolesConst.SuperAdmin,
                    then: () => Yup.number().required('required'),
                    otherwise: () => Yup.number().nullable()
                }),
                salaryPerMonth: Yup.number().when('role', {
                    is: (role) => role === rolesConst.Follow || role === rolesConst.Management,
                    then: () => Yup.number().required('required'),
                    otherwise: () => Yup.number().nullable()
                }),
                allowedProductIds: Yup.array().when('role', {
                    is: (role) => [...confirmationRoles, rolesConst.Management].includes(role),
                    then: () => Yup.array().min(1, 'At least one product must be selected').required('Products are required'),
                    otherwise: () => Yup.array().nullable()
                }),
            })}
            handleSubmitForm={(values, submitProps) => {
                saveUser(values, submitProps);
            }}
        >
            <Row gutter={[16, 16]}>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <TextInput
                        name="name"
                        placeholder="Nom"
                        size="large"
                    />
                </Col>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <TextInput
                        name="prefix"
                        placeholder="Prefix"
                        size="large"
                    />
                </Col>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <TextInput
                        name="email"
                        placeholder="E-mail"
                        size="large"
                    />
                </Col>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <TextInput
                        name="password"
                        placeholder="Mot de passe"
                        size="large"
                    />
                </Col>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <TextInput
                        name="phone"
                        placeholder="Téléphone"
                        size="large"
                    />
                </Col>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <TextInput
                        name="city"
                        placeholder="Ville"
                        size="large"
                    />
                </Col>
                <Col xl={12} md={12} sm={24} xs={24}>
                    <SelectInput
                        name="role"
                        size="large"
                        placeholder="Role"
                        options={roles.map(role => ({
                            value: role,
                            label: role
                        }))}
                        onChangeValue={(value) => setSelectedRole(value)}
                    />
                </Col>
                {confirmationRoles.includes(selectedRole) && (
                    <Col xl={12} md={12} sm={24} xs={24}>
                        <SelectInput
                            name="managerId"
                            size="large"
                            placeholder="Manager"
                            options={managers}
                        />
                    </Col>
                )}
                {[...confirmationRoles, rolesConst.Management].includes(selectedRole) && (
                    <>
                        <Col xl={24} md={24} sm={24} xs={24}>
                            <MultipleSelect
                                name="deliveryCompanyIds"
                                placeholder="Sociétés de livraison"
                                size="large"
                                options={deliveryCompanies}
                            />
                        </Col>
                        <Col xl={24} md={24} sm={24} xs={24}>
                            <MultipleSelect
                                name="deliveryCompanyKeyIds"
                                placeholder="Comptes de livraison"
                                size="large"
                                options={deliveryCompanyKeys}
                            />
                        </Col>
                    </>
                )}
                {[...confirmationRoles, rolesConst.Management, rolesConst.Deliveryman].includes(selectedRole) && (
                    <Col xl={12} md={12} sm={24} xs={24}>
                        <TextInput
                            name="paymentPerOrder"
                            placeholder="Paiement par commande"
                            size="large"
                        />
                    </Col>
                )}
                {[...confirmationRoles, rolesConst.Management].includes(selectedRole) && (
                    <>
                        <Col xl={12} md={12} sm={24} xs={24}>
                            <TextInput
                                name="paymentPerUnitInOrder"
                                placeholder="Paiement par unité"
                                size="large"
                            />
                        </Col>
                        <Col xl={12} md={12} sm={24} xs={24}>
                            <TextInput
                                name="paymentPerMoreThenOneProductInOrder"
                                placeholder="Paiement par upsell"
                                size="large"
                            />
                        </Col>
                    </>
                )}
                {ManagementRoles.includes(selectedRole) && (
                    <Col xl={12} md={12} sm={24} xs={24}>
                        <TextInput
                            name="salaryPerMonth"
                            placeholder="Salair par mois"
                            size="large"
                        />
                    </Col>
                )}
                {[...confirmationRoles, rolesConst.Management].includes(selectedRole) && (
                    <Col xl={24} md={24} sm={24} xs={24}>
                        <MultipleSelect
                            name="allowedProductIds"
                            placeholder="Produits autorisés"
                            size="large"
                            options={products}
                        />
                    </Col>
                )}
                <Col xl={12} md={12} sm={24} xs={24}>
                    <Button
                        size='large'
                        type="primary"
                        htmlType="submit"
                        style={{ width: "100%" }}
                    >
                        {initialValue.id ? "Mis a jour" : "Ajouter"}
                    </Button>
                </Col>
            </Row>
        </AddElementLayout>
    );
}

export default AddUser;